import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { adminListPosts } from "@/lib/blog-admin.functions";
import {
  adminWeeklyBatchStatus,
  adminRefreshGscNow,
  adminBuildWeeklyGscDrafts,
  adminRunSaturdayBatchNow,
  adminWeekCompleteness,
  adminRepairWeek,
} from "@/lib/blog-weekly-batch.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/blog-schedule")({
  component: Schedule,
});

const DAY_LABEL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Schedule() {
  const list = useServerFn(adminListPosts);
  const status = useServerFn(adminWeeklyBatchStatus);
  const refreshGsc = useServerFn(adminRefreshGscNow);
  const buildGsc = useServerFn(adminBuildWeeklyGscDrafts);
  const runSaturday = useServerFn(adminRunSaturdayBatchNow);
  const weekReport = useServerFn(adminWeekCompleteness);
  const repairWeek = useServerFn(adminRepairWeek);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["admin-posts"], queryFn: () => list() });
  const w = useQuery({ queryKey: ["admin-weekly-batch"], queryFn: () => status() });
  const r = useQuery({ queryKey: ["admin-week-completeness"], queryFn: () => weekReport() });

  const upcoming = ((q.data ?? []) as any[])
    .filter((p) => p.status === "scheduled" && p.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());

  const run = async (key: string, fn: () => Promise<any>, label: string) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(`${label}: ${JSON.stringify(res).slice(0, 160)}`);
      await qc.invalidateQueries({ queryKey: ["admin-weekly-batch"] });
      await qc.invalidateQueries({ queryKey: ["admin-week-completeness"] });
    } catch (e) {
      toast.error((e as Error)?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  };

  const data = w.data as any;

  return (
    <div>
      <BlogSubNav />
      <h1 className="font-display text-2xl font-bold">Weekly batch &amp; publishing schedule</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        All automated generation happens in one Saturday run and is always saved as a draft.
      </p>


      {r.data && (
        <section className="mt-6 rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold">
                This week&apos;s coverage: {(r.data as any).created}/{(r.data as any).expected} drafts
              </h2>
              <p className="text-xs text-muted-foreground">
                Week of {(r.data as any).week} · {(r.data as any).missing} missing · {(r.data as any).blocked} blocked ·{" "}
                {(r.data as any).complete ? "complete" : "incomplete"}
              </p>
            </div>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => run("repair", () => repairWeek(), "Week repaired (drafts only)")}
            >
              Repair week
            </Button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Day</th>
                  <th className="py-1">Lane</th>
                  <th className="py-1">Article</th>
                  <th className="py-1">State</th>
                </tr>
              </thead>
              <tbody>
                {(r.data as any).slots.map((s: any) => (
                  <tr key={`${s.lane}-${s.planned_date}`} className="border-t border-border/60">
                    <td className="py-2 align-top">
                      {DAY_LABEL[s.planned_day - 1]} {s.planned_date}
                    </td>
                    <td className="py-2 align-top text-xs text-muted-foreground">{s.role}</td>
                    <td className="py-2 align-top text-xs">
                      {s.post ? (
                        <>
                          {s.post.title}
                          <span className="ml-2 text-muted-foreground">
                            {s.post.status} · tech {s.post.technical_seo_score ?? "—"} ·{" "}
                            {s.post.quality_ready ? "ready" : `${s.post.blockers.length} blocker(s)`}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 align-top text-xs">
                      {s.state === "created" ? (
                        <span className="text-emerald-700">created</span>
                      ) : (
                        <span className="text-amber-700">{s.state}: {s.reason ?? "not drafted"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(r.data as any).invariants?.violations?.length > 0 && (
            <ul className="mt-3 list-disc pl-5 text-xs text-destructive">
              {(r.data as any).invariants.violations.map((i: string) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            Search Console data freshness:{" "}
            {(r.data as any).gsc?.last_refresh_at
              ? `${new Date((r.data as any).gsc.last_refresh_at).toLocaleString()} (${(r.data as any).gsc.fresh ? "fresh" : "stale"})`
              : "no stored refresh yet"}
            . A day stays empty rather than being filled with invented data.
          </p>
        </section>
      )}

      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">Saturday weekly batch</h2>
            <p className="text-xs text-muted-foreground">
              {data
                ? `Week of ${data.week} · next run ${new Date(data.nextSaturdayRun).toLocaleString()} · auto-publish ${
                    data.autoPublish ? "ON" : "OFF"
                  }`
                : "Loading…"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => run("refresh", () => refreshGsc(), "Search Console refreshed")}
            >
              Refresh GSC now
            </Button>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => run("gsc", () => buildGsc(), "GSC drafts built")}
            >
              Build this week&apos;s GSC drafts
            </Button>
            <Button
              disabled={busy !== null}
              onClick={() =>
                run("saturday", () => runSaturday(), "Saturday batch complete (drafts only)")
              }
            >
              Run Saturday batch now
            </Button>
          </div>
        </div>


        {data && (
          <>
            <p className="mt-4 text-xs text-muted-foreground">
              Search Console:{" "}
              {data.gsc.connected
                ? "connected"
                : "not connected yet — expected before launch. Daily refresh stays on and will start pulling real data automatically once the live site is connected."}
              {data.gsc.connected && (
                <>
                  {" "}· last refresh{" "}
                  {data.gsc.lastRefreshAt ? new Date(data.gsc.lastRefreshAt).toLocaleString() : "never"} · window{" "}
                  {data.gsc.window.start ?? "—"} → {data.gsc.window.end ?? "—"} · {data.gsc.usable} usable /{" "}
                  {data.gsc.rejected} rejected
                </>
              )}
            </p>
            <div className="mt-4 grid gap-2">
              {data.slots.map((s: any) => (
                <div key={s.planned_date} className="rounded-lg border border-border p-3 text-sm">
                  <p className="font-medium">
                    {DAY_LABEL[s.planned_day - 1]} {s.planned_date} — {s.role}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Cluster: {s.cluster ? `${s.cluster.title} (${s.cluster.status})` : "not drafted"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    GSC: {s.gsc ? `${s.gsc.title} — "${s.gsc.gsc_query}"` : data.gsc.connected ? "no valid opportunity" : "Search Console not connected yet"}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {data.counts.cluster}/7 cluster drafts · {data.counts.gsc}/7 Search Console drafts this week. Empty days
              mean there was no valid real Search Console data — nothing is ever invented to fill a gap.
            </p>
          </>
        )}
      </section>

      <h2 className="mt-8 font-semibold">Upcoming scheduled posts</h2>
      <div className="mt-3 space-y-3">
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
        ) : (
          upcoming.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <div>
                <p className="font-semibold">{p.title}</p>
                <p className="text-xs text-muted-foreground">/{p.slug}</p>
              </div>
              <p className="text-sm">{new Date(p.scheduled_at).toLocaleString()}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
